/**
 * Centralized eFax Corporate brand palette — DARK theme.
 * eFax red accent on a near-black background, light text.
 */
export const theme = {
  primary: '#FF4133', // eFax red, slightly brightened for contrast on dark
  primaryDark: '#C62828', // darker red (hover/active)
  // Header bar. Deliberately black rather than brand red: the official eFax
  // gradient logo (red -> purple -> blue) sits on it, and red-on-red muddied the
  // mark's own red end. Black lets the whole gradient read cleanly.
  headerBg: '#000000',
  text: '#f5f5f5', // near-white body text
  textMuted: '#9aa0a6', // muted gray for secondary text
  bg: '#121212', // app background (near-black)
  bgAlt: '#1e1e1e', // elevated surface / cards
  bgElevated: '#262626', // table headers, hover rows
  border: '#333333', // dividers/borders on dark
  onPrimary: '#ffffff',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',

  /**
   * CHART SERIES PALETTE — added for the Reports charts (W4-4B). Read these instead of
   * writing a hex into a chart: a series colour is a piece of shared vocabulary, and two
   * pages disagreeing about which colour means "inbound leg" is worse than either choice.
   *
   * NOT THE BRAND RED, AND NOT RED/GREEN. `primary` (#FF4133) is the action colour — it means
   * "you can click this" everywhere else in the app, so spending it on a data series makes a
   * measurement look interactive. More importantly, a red/green split is invisible to roughly
   * one man in twelve, and this page's whole job is telling one leg from the other.
   *
   * These are Okabe-Ito values, a palette designed to stay mutually distinguishable under
   * protanopia, deuteranopia and tritanopia. Blue against orange-red is the canonical safe
   * pairing; red against green is the one to avoid.
   *
   * THEY ALSO DIFFER IN LUMINANCE, which hue-safety alone does NOT give you. The first draft of
   * this palette used Okabe-Ito sky blue (#56B4E9) against orange (#E69F00) — CVD-safe, and yet
   * their relative luminances are 163.8 and 162.6, a difference of 1.2 out of 255. Those two
   * are the SAME GREY. They would have been indistinguishable on a greyscale print, on a
   * failing projector, and to anyone with achromatopsia, while every accessibility note above
   * claimed otherwise. `test_the_legs_are_not_distinguished_by_red_against_green` measures the
   * separation rather than trusting the claim, and it is what caught this.
   *
   * COLOUR IS NEVER THE ONLY CARRIER. Each series also gets a dash pattern (`dash`) and,
   * where the mark is filled, a hatch direction; the legend and tooltip name every series in
   * words. A reader who cannot see any difference in hue can still read the chart.
   */
  chart: {
    /** Outbound leg: submit -> provider reported sent. Sky blue, solid, no hatch. Luminance ~164. */
    outbound: '#56B4E9',
    /** Inbound leg: sent -> received. Vermillion, dashed, hatched. Luminance ~113 — see above. */
    inbound: '#D55E00',
    /** Quality score trend. Yellow: luminance ~219, clear of both legs and of the background. */
    quality: '#F0E442',
    /** Reddish purple, for a threshold or reference line. Not a leg. */
    reference: '#CC79A7',
    /** Axis lines and labels. */
    axis: '#9aa0a6',
    /** Grid lines: present enough to read a value against, faint enough not to be a series. */
    grid: '#2f2f2f',
    /** Tooltip surface, above `bgAlt` so it reads as floating over a card. */
    tooltipBg: '#0b0b0b',
    /**
     * Dash patterns, as SVG stroke-dasharray. The SECOND, non-colour carrier of series
     * identity. `outbound` is deliberately solid so the default reading is the simplest one.
     */
    dash: {
      outbound: '0',
      inbound: '6 3',
      quality: '0',
    },
  },
} as const;

export default theme;
